import { useState, useEffect } from "react";
import Button from "components/Button";
import FormField from "components/FormField";
import userService from "services/user.service";
import { isValidTimeFormat } from "pages/RfqNotifications/utils/validation";
import styles from "./ScheduleModal.module.scss";

const DAYS_OF_WEEK = [
  { value: 0, label: "Monday" },
  { value: 1, label: "Tuesday" },
  { value: 2, label: "Wednesday" },
  { value: 3, label: "Thursday" },
  { value: 4, label: "Friday" },
  { value: 5, label: "Saturday" },
  { value: 6, label: "Sunday" }
];

const ScheduleModal = ({ isOpen, schedule, dayIndex, existingSchedules = [], onSave, onClose, loading = false }) => {
  const [formData, setFormData] = useState({
    day_of_week: dayIndex !== null ? dayIndex : 0,
    start_time: "09:00",
    end_time: "17:00"
  });
  const [errors, setErrors] = useState({});
  const [userTimezone, setUserTimezone] = useState(null);
  const [timezoneLoading, setTimezoneLoading] = useState(false);

  useEffect(() => {
    if (schedule) {
      setFormData({
        day_of_week: schedule.day_of_week,
        start_time: schedule.start_time ? schedule.start_time.substring(0, 5) : "09:00",
        end_time: schedule.end_time ? schedule.end_time.substring(0, 5) : "17:00"
      });
    } else if (dayIndex !== null) {
      setFormData({
        day_of_week: dayIndex,
        start_time: "09:00",
        end_time: "17:00"
      });
    }
  }, [schedule, dayIndex]);

  // Fetch user timezone when modal opens
  useEffect(() => {
    if (isOpen && !userTimezone && !timezoneLoading) {
      setTimezoneLoading(true);
      userService.getUserTimezone()
        .then(response => {
          setUserTimezone(response.data);
        })
        .catch(error => {
          console.error('Failed to fetch user timezone:', error);
          // Set fallback timezone
          setUserTimezone({ timezone: 'US/Pacific', display_name: 'Pacific Time (PST/PDT)' });
        })
        .finally(() => {
          setTimezoneLoading(false);
        });
    }
  }, [isOpen, userTimezone, timezoneLoading]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear field error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Validate time format
    if (!isValidTimeFormat(formData.start_time)) {
      newErrors.start_time = "Invalid time format (HH:MM)";
    }
    if (!isValidTimeFormat(formData.end_time)) {
      newErrors.end_time = "Invalid time format (HH:MM)";
    }

    // Check for duplicate day (only if this is a new schedule or changing day)
    const isDuplicateDay = existingSchedules.some(s => 
      s.day_of_week === parseInt(formData.day_of_week) && 
      (!schedule || s.day_of_week !== schedule.day_of_week)
    );
    
    if (isDuplicateDay) {
      newErrors.day_of_week = `Schedule for ${DAYS_OF_WEEK[formData.day_of_week].label} already exists`;
    }

    // Check if start and end times are the same
    if (formData.start_time === formData.end_time) {
      newErrors.end_time = "End time must be different from start time";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      const scheduleData = {
        day_of_week: parseInt(formData.day_of_week),
        start_time: formData.start_time + ":00", // Add seconds for consistency
        end_time: formData.end_time + ":00"
      };
      
      // Include the schedule ID if we're editing an existing schedule
      if (schedule?.id) {
        scheduleData.id = schedule.id;
      }
      
      onSave(scheduleData);
    }
  };

  const handleClose = () => {
    setFormData({
      day_of_week: dayIndex !== null ? dayIndex : 0,
      start_time: "09:00",
      end_time: "17:00"
    });
    setErrors({});
    onClose();
  };

  const getAvailableDays = () => {
    const existingDays = existingSchedules.map(s => s.day_of_week);
    return DAYS_OF_WEEK.filter(day => 
      !existingDays.includes(day.value) || 
      (schedule && day.value === schedule.day_of_week)
    );
  };

  const isOvernightSchedule = formData.start_time > formData.end_time;

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <h3>{schedule ? "Edit Schedule" : "Add Schedule"}</h3>
          <button 
            type="button" 
            className={styles.closeButton}
            onClick={handleClose}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className={styles.scheduleForm}>
          <div className={styles.formField}>
            <label htmlFor="day_of_week">Day of Week</label>
            {dayIndex !== null && !schedule ? (
              // Adding new schedule for specific day - show as read-only
              <div className={styles.readOnlyDay}>
                {DAYS_OF_WEEK[dayIndex].label}
              </div>
            ) : (
              // Editing existing schedule - show dropdown
              <select
                id="day_of_week"
                value={formData.day_of_week}
                onChange={(e) => handleInputChange("day_of_week", e.target.value)}
                className={errors.day_of_week ? styles.errorInput : ''}
              >
                {getAvailableDays().map(day => (
                  <option key={day.value} value={day.value}>
                    {day.label}
                  </option>
                ))}
              </select>
            )}
            {errors.day_of_week && (
              <div className={styles.fieldError}>{errors.day_of_week}</div>
            )}
          </div>

          {/* Timezone display */}
          <div className={styles.timezoneInfo}>
            <span className={styles.timezoneLabel}>Timezone:</span>
            <span className={styles.timezoneValue}>
              {timezoneLoading 
                ? 'Loading timezone...'
                : userTimezone 
                  ? userTimezone.display_name || userTimezone.timezone || 'Pacific Time (PST/PDT)'
                  : 'Pacific Time (PST/PDT)'
              }
            </span>
          </div>

          <div className={styles.timePickers}>
            <div className={styles.formField}>
              <label htmlFor="start_time">Start Time</label>
              <input
                id="start_time"
                type="time"
                value={formData.start_time}
                onChange={(e) => handleInputChange("start_time", e.target.value)}
                step="900"
                className={errors.start_time ? styles.errorInput : ''}
              />
              {errors.start_time && (
                <div className={styles.fieldError}>{errors.start_time}</div>
              )}
            </div>

            <div className={styles.formField}>
              <label htmlFor="end_time">End Time</label>
              <input
                id="end_time"
                type="time"
                value={formData.end_time}
                onChange={(e) => handleInputChange("end_time", e.target.value)}
                step="900"
                className={errors.end_time ? styles.errorInput : ''}
              />
              {errors.end_time && (
                <div className={styles.fieldError}>{errors.end_time}</div>
              )}
            </div>
          </div>

          {isOvernightSchedule && (
            <div className={styles.overnightWarning}>
              <span className={styles.warningIcon}>⚠️</span>
              This is an overnight schedule (ends next day)
            </div>
          )}

          <div className={styles.modalActions}>
            <Button 
              type="button" 
              onClick={handleClose} 
              variant="outline"
              disabled={loading}
            >
              Cancel
            </Button>
            <Button 
              type="submit"
              disabled={loading}
            >
              {loading 
                ? "Saving..." 
                : (schedule ? "Update Schedule" : "Add Schedule")
              }
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ScheduleModal;